import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Modal, ModalBody, Input, Label, Button } from "reactstrap";
import SearchDropdown from "../searchDropdown";
import DateRangeFilter, { rangeFromPreset, DEFAULT_PRESETS } from "../dateRangeFilter/DateRangeFilter";
import { showToast } from "../../utils/showToast";
import { getRegions, getCommunes } from "../../api/contracts";
import { useCountry } from "../../context/CountryContext";
import { getHoldingCompanies, searchHoldingSenders } from "../../api/holding";
import { debounce } from "../../utils/debounce";
import {
  Filter,
  X,
  Calendar,
  MapPin,
  User,
  Package,
  RotateCcw,
  ArrowUpDown,
} from "lucide-react";
import { packageStatusFilterOptions } from "../../utils/packageStatus";

// TODO: migrar a ui/Modal — actualmente usa reactstrap <Modal>/<ModalBody> y
// SCSS específico (`.modern-filter-modal`). Migrar requiere reimplementar
// header/footer con el ui/Modal y revisar la accesibilidad del autocomplete
// de senders. Edge case: el dropdown debe poder sobresalir del panel (overflow
// visible) sin romper el scroll-lock del backdrop. Pendiente de pase aparte.
/**
 * Clon de `FilterModal` para el perfil "admin de holding":
 *   - Elimina la sección "Asignación" (Cliente + Repartidor) y "Discrepancia MercadoLibre".
 *   - Agrega sección "Cliente" con:
 *       - Vendedor: dropdown con las companies hijas del holding (`getHoldingCompanies`).
 *       - Sender ID: autocomplete contra `companyintegrations.integrations.sender_id`
 *         vía `searchHoldingSenders`. Al seleccionar, también setea `vendorCompanyId`
 *         con el companyId asociado al sender (porque microservice-packages filtra por companyId).
 *
 * Comparte el contrato de filtros (`callback`, `activeFilters`, etc.) con `FilterModal`.
 */
const HoldingFilterModal = ({ open, activeFilters, callback, closeModal, statusDisabled }) => {
  const { t } = useTranslation();
  const [filters, setFilters] = useState(activeFilters || {});
  const [companiesList, setCompaniesList] = useState([]);
  const [regionsList, setRegionsList] = useState([]);
  const [communesList, setCommunesList] = useState([]);
  const [senderSuggestions, setSenderSuggestions] = useState([]);
  const [senderQuery, setSenderQuery] = useState(activeFilters?.senderLabel || "");
  const [isLoadingCompanies, setIsLoadingCompanies] = useState(false);
  const [isLoadingRegions, setIsLoadingRegions] = useState(false);
  const [isLoadingCommunes, setIsLoadingCommunes] = useState(false);
  const [isLoadingSenders, setIsLoadingSenders] = useState(false);
  const [showSenderDropdown, setShowSenderDropdown] = useState(false);
  const senderWrapperRef = useRef(null);

  const prevOpen = useRef(open);
  // Mismo contrato que FilterModal: labels de las divisiones administrativas
  // del país del operador y flag de código postal.
  const {
    countryId,
    loading: countryLoading,
    labels,
    postalCodeRates,
  } = useCountry();
  const showPostalCode = postalCodeRates?.enabled === true;

  // Sync con activeFilters cuando se abre el modal
  useEffect(() => {
    if (open && !prevOpen.current) {
      setFilters(activeFilters || {});
      setSenderQuery(activeFilters?.senderLabel || "");
    }
    prevOpen.current = open;
  }, [open, activeFilters]);

  // Cargar companies hijas del holding y regiones cuando abre.
  useEffect(() => {
    const fetchCompanies = async () => {
      setIsLoadingCompanies(true);
      try {
        const res = await getHoldingCompanies({ page: 1, limit: 200 });
        if (!res?.error && Array.isArray(res?.docs)) {
          setCompaniesList(
            res.docs.map((c) => ({
              value: c._id,
              label: `${c.name}${c.businessIdentifier ? ` · ${c.businessIdentifier}` : ""}`,
            })),
          );
        }
      } catch (e) {
        console.error("Error cargando companies del holding:", e);
        showToast(t("Error al cargar empresas del holding"), false);
      } finally {
        setIsLoadingCompanies(false);
      }
    };

    const fetchRegions = async () => {
      setIsLoadingRegions(true);
      try {
        const data = await getRegions({ countryId });
        if (!data?.error && Array.isArray(data)) {
          setRegionsList(data.map((r) => ({ value: r.code, label: r.name })));
        }
      } catch (e) {
        console.error("Error al obtener regiones:", e);
        showToast(t("Error al obtener regiones"), false);
      } finally {
        setIsLoadingRegions(false);
      }
    };

    if (open) {
      fetchCompanies();
      // Sin país resuelto no se piden regiones; el efecto vuelve a correr
      // cuando `countryId` llega.
      if (!countryLoading && countryId) fetchRegions();
    }
  }, [open, countryId, countryLoading]);

  useEffect(() => {
    const fetchCommunes = async () => {
      if (filters.regionCode && open && countryId) {
        setIsLoadingCommunes(true);
        try {
          const data = await getCommunes({ countryId, regionId: filters.regionCode });
          if (!data?.error) {
            setCommunesList(data.map((c) => ({ value: c.name, label: c.name })));
          }
        } catch (e) {
          console.error("Error al obtener comunas:", e);
          showToast(t("Error al obtener comunas"), false);
        } finally {
          setIsLoadingCommunes(false);
        }
      } else {
        setCommunesList([]);
      }
    };
    fetchCommunes();
  }, [filters.regionCode, open, countryId]);

  // Autocomplete de senders con debounce 300ms
  const runSenderSearch = useCallback(async (q) => {
    if (!q || q.trim().length < 1) {
      setSenderSuggestions([]);
      setIsLoadingSenders(false);
      return;
    }
    setIsLoadingSenders(true);
    try {
      const res = await searchHoldingSenders(q.trim(), 20);
      setSenderSuggestions(Array.isArray(res) ? res : []);
    } catch (e) {
      setSenderSuggestions([]);
    } finally {
      setIsLoadingSenders(false);
    }
  }, []);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSenderSearch = useMemo(() => debounce(runSenderSearch, 300), [runSenderSearch]);

  useEffect(() => {
    if (showSenderDropdown && senderQuery && senderQuery.trim().length >= 1) {
      debouncedSenderSearch(senderQuery);
    } else if (!senderQuery) {
      setSenderSuggestions([]);
    }
  }, [senderQuery, showSenderDropdown, debouncedSenderSearch]);

  // Cerrar el dropdown del autocomplete al clickear fuera
  useEffect(() => {
    const onClickOutside = (e) => {
      if (senderWrapperRef.current && !senderWrapperRef.current.contains(e.target)) {
        setShowSenderDropdown(false);
      }
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);

  const onSenderSelect = (item) => {
    setFilters({
      ...filters,
      // microservice-packages filtra por companyId; el sender_id se convierte a companyId aquí.
      vendorCompanyId: item.companyId,
      senderId: item.sender_id,
      senderLabel: `${item.sender_id} · ${item.companyName}${item.integrationName ? ` · ${item.integrationName}` : ""}`,
    });
    setSenderQuery(`${item.sender_id} · ${item.companyName}`);
    setShowSenderDropdown(false);
  };

  const onSenderClear = () => {
    setSenderQuery("");
    setFilters({
      ...filters,
      vendorCompanyId: filters.vendorCompanyId === filters.senderCompanyIdLink ? undefined : filters.vendorCompanyId,
      senderId: undefined,
      senderLabel: undefined,
    });
    setSenderSuggestions([]);
    setShowSenderDropdown(false);
  };

  const formatMMDDYYYY = (yyyyMmDd) => {
    if (!yyyyMmDd) return undefined;
    const [y, m, d] = yyyyMmDd.split("-").map((n) => parseInt(n, 10));
    const mm = String(m).padStart(2, "0");
    const dd = String(d).padStart(2, "0");
    return `${mm}/${dd}/${y}`;
  };

  const applyFilters = () => {
    const { dateStart, dateEnd } = filters || {};

    if (dateStart && dateEnd && new Date(dateEnd) < new Date(dateStart)) {
      showToast(t("La fecha Fin no puede ser menor que Inicio."), false);
      return;
    }

    const outgoing = { ...filters };
    outgoing.dateStart = dateStart || "";
    outgoing.dateEnd = dateEnd || "";

    if (dateStart) outgoing.startDateCreation = formatMMDDYYYY(dateStart);
    else delete outgoing.startDateCreation;

    if (dateEnd) outgoing.endDateCreation = formatMMDDYYYY(dateEnd);
    else delete outgoing.endDateCreation;

    delete outgoing.updatedAtSince;

    callback(outgoing);
    closeModal(false);
  };

  const clearFilters = () => {
    setFilters({});
    setSenderQuery("");
    setSenderSuggestions([]);
    callback({});
    closeModal(false);
  };

  const orderBy = [
    { value: "createdAt", label: t("Fecha de ingreso") },
    { value: "updatedAt", label: t("Última actualización") },
  ];

  const selectedOrder = orderBy?.find((f) => f.value === filters?.orderBy);
  const selectedRegion = regionsList?.find((f) => f.value === filters?.regionCode);
  const selectedCom = communesList?.find((f) => f.value === filters?.coms);
  const selectedCompany = companiesList?.find((f) => f.value === filters?.vendorCompanyId);

  // Preset activo del rango "Fecha de ingreso" (resalta el chip o "custom").
  // dateStart/dateEnd siguen siendo YMD; la derivación a startDateCreation/
  // endDateCreation en applyFilters queda intacta.
  const datePreset = useMemo(() => {
    const ds = filters?.dateStart;
    const de = filters?.dateEnd;
    if (!ds || !de) return "";
    const match = DEFAULT_PRESETS.find(({ key }) => {
      const [s, e] = rangeFromPreset(key);
      return s.format("YYYY-MM-DD") === ds && e.format("YYYY-MM-DD") === de;
    });
    return match ? match.key : "custom";
  }, [filters?.dateStart, filters?.dateEnd]);

  const hasActiveFilters = useMemo(() => {
    if (!filters || typeof filters !== "object") return false;
    return Object.entries(filters).some(([k, v]) => {
      if (v === undefined || v === null || v === "") return false;
      if (k === "status" && String(v) === "") return false;
      if (k === "senderLabel") return false; // visual only
      return true;
    });
  }, [filters]);

  const fieldClass = (active) =>
    `modern-filter-modal__field${active ? " modern-filter-modal__field--active" : ""}`;

  const fieldItemClass = (active) =>
    `modern-filter-modal__field-item${active ? " modern-filter-modal__field-item--active" : ""}`;

  return (
    <Modal
      isOpen={open}
      toggle={() => closeModal(false)}
      centered
      className="modern-filter-modal"
      fullscreen="sm"
      size="lg"
      role="dialog"
      aria-labelledby="filter-modal-title"
      aria-modal="true"
    >
      <ModalBody className="modern-filter-modal__body">
        <div className="modern-filter-modal__header">
          <div className="modern-filter-modal__header-title">
            <Filter size={20} className="modern-filter-modal__title-icon" />
            <h2 id="filter-modal-title" className="modern-filter-modal__title">
              {t("Filtrar y ordenar")}
            </h2>
          </div>
          <button
            className="modern-filter-modal__close"
            onClick={() => closeModal(false)}
            aria-label={t("Cerrar")}
            type="button"
          >
            <X size={18} />
          </button>
        </div>

        <div className="modern-filter-modal__content">
          {/* Ordenamiento + Estado */}
          <div className="modern-filter-modal__field">
            <div className="modern-filter-modal__row">
              <div className={fieldItemClass(!!filters?.orderBy)} role="group" aria-labelledby="filter-section-order-label">
                <div className="modern-filter-modal__field-header" id="filter-section-order-label">
                  <ArrowUpDown size={16} className="modern-filter-modal__field-icon" aria-hidden />
                  <Label className="modern-filter-modal__label">{t("Ordenar por")}</Label>
                </div>
                <SearchDropdown
                  onChange={(value) => setFilters({ ...filters, orderBy: value })}
                  options={orderBy}
                  placeholder={t("Seleccione...")}
                  selected={selectedOrder?.label}
                  ariaLabel={t("Ordenar resultados por campo")}
                />
              </div>

              <div
                className={fieldItemClass(filters?.status !== undefined && filters?.status !== "")}
                role="group"
                aria-labelledby="filter-section-status-label"
              >
                <div className="modern-filter-modal__field-header" id="filter-section-status-label">
                  <Package size={16} className="modern-filter-modal__field-icon" aria-hidden />
                  <Label className="modern-filter-modal__label">{t("Estado del paquete")}</Label>
                </div>
                <SearchDropdown
                  onChange={(value) => setFilters({ ...filters, status: value })}
                  options={[{ value: "", label: t("Todos los estados") }, ...packageStatusFilterOptions]}
                  placeholder={t("Seleccione un estado...")}
                  selected={
                    filters?.status !== undefined && filters?.status !== ""
                      ? [{ value: "", label: t("Todos los estados") }, ...packageStatusFilterOptions].find(
                          (s) => String(s.value) === String(filters?.status),
                        )?.label
                      : ""
                  }
                  disabled={statusDisabled}
                  ariaLabel={t("Filtrar por estado del paquete")}
                />
              </div>
            </div>
          </div>

          {/* Ubicación */}
          <div
            className={fieldClass(!!filters?.regionCode || !!filters?.coms || !!filters?.zipCode)}
            role="group"
            aria-labelledby="filter-section-location-label"
          >
            <div className="modern-filter-modal__field-header" id="filter-section-location-label">
              <MapPin size={16} className="modern-filter-modal__field-icon" aria-hidden />
              <Label className="modern-filter-modal__label">{t("Ubicación de entrega")}</Label>
            </div>
            <div className="modern-filter-modal__row">
              <div className="modern-filter-modal__field-item">
                <Label className="modern-filter-modal__sub-label">{t(labels.region)}</Label>
                <SearchDropdown
                  onChange={(value) => setFilters({ ...filters, regionCode: value, coms: undefined })}
                  options={regionsList}
                  placeholder={isLoadingRegions ? t("Cargando...") : t("Seleccione...")}
                  selected={selectedRegion?.label}
                  disabled={isLoadingRegions}
                  ariaLabel={t("Seleccionar {{region}} de entrega", { region: labels.region.toLowerCase() })}
                  ariaBusy={isLoadingRegions}
                />
              </div>
              <div className="modern-filter-modal__field-item">
                <Label className="modern-filter-modal__sub-label">{t(labels.commune)}</Label>
                <SearchDropdown
                  onChange={(value) => setFilters({ ...filters, coms: value })}
                  options={communesList}
                  placeholder={
                    isLoadingCommunes
                      ? t("Cargando...")
                      : filters.regionCode
                        ? t("Seleccione...")
                        : `${t("Primero seleccione")} ${labels.region.toLowerCase()}`
                  }
                  selected={selectedCom?.label}
                  disabled={isLoadingCommunes || !filters.regionCode}
                  ariaLabel={t("Seleccionar {{commune}} de entrega", { commune: labels.commune.toLowerCase() })}
                  ariaBusy={isLoadingCommunes}
                />
              </div>
              {/* Código postal: solo en países que lo usan como clave operativa. */}
              {showPostalCode && (
                <div className="modern-filter-modal__field-item">
                  <Label className="modern-filter-modal__sub-label" for="holding-filter-zip-code">
                    {t("Código postal")}
                  </Label>
                  <Input
                    id="holding-filter-zip-code"
                    type="text"
                    value={filters?.zipCode || ""}
                    maxLength={12}
                    onChange={(e) =>
                      setFilters({
                        ...filters,
                        zipCode: e.target.value.toUpperCase().slice(0, 12) || undefined,
                      })
                    }
                    placeholder={t("Ej: 5501 o M5501ABC")}
                    aria-label={t("Filtrar por código postal de entrega")}
                  />
                </div>
              )}
            </div>
          </div>

          {/* Cliente (reemplaza la sección Asignación admin) */}
          <div
            className={fieldClass(!!filters?.vendorCompanyId || !!filters?.senderId)}
            role="group"
            aria-labelledby="filter-section-customer-label"
          >
            <div className="modern-filter-modal__field-header" id="filter-section-customer-label">
              <User size={16} className="modern-filter-modal__field-icon" aria-hidden />
              <Label className="modern-filter-modal__label">{t("Cliente")}</Label>
            </div>
            <div className="modern-filter-modal__row">
              <div className="modern-filter-modal__field-item">
                <Label className="modern-filter-modal__sub-label">{t("Vendedor")}</Label>
                <SearchDropdown
                  onChange={(value) => setFilters({ ...filters, vendorCompanyId: value })}
                  options={companiesList}
                  placeholder={isLoadingCompanies ? t("Cargando...") : t("Seleccione empresa hija...")}
                  selected={selectedCompany?.label}
                  disabled={isLoadingCompanies}
                  tooltip
                  ariaLabel={t("Filtrar por vendedor (empresa hija)")}
                  ariaBusy={isLoadingCompanies}
                />
              </div>

              <div className="modern-filter-modal__field-item" ref={senderWrapperRef} style={{ position: "relative" }}>
                <Label className="modern-filter-modal__sub-label">{t("Sender ID")}</Label>
                <div style={{ position: "relative" }}>
                  <Input
                    type="text"
                    value={senderQuery}
                    placeholder={t("Ej: 567279")}
                    onChange={(e) => {
                      setSenderQuery(e.target.value);
                      setShowSenderDropdown(true);
                    }}
                    onFocus={() => setShowSenderDropdown(true)}
                    aria-label={t("Buscar por sender_id de integración")}
                    aria-autocomplete="list"
                    aria-expanded={showSenderDropdown}
                  />
                  {senderQuery && (
                    <button
                      type="button"
                      onClick={onSenderClear}
                      aria-label={t("Limpiar sender_id")}
                      style={{
                        position: "absolute",
                        right: 8,
                        top: "50%",
                        transform: "translateY(-50%)",
                        border: "none",
                        background: "transparent",
                        cursor: "pointer",
                      }}
                    >
                      <X size={14} />
                    </button>
                  )}
                </div>
                {showSenderDropdown && (senderSuggestions.length > 0 || isLoadingSenders) && (
                  <ul
                    role="listbox"
                    className="absolute left-0 right-0 top-full mt-1 max-h-[220px] overflow-y-auto m-0 p-1 list-none rounded-md border border-border bg-bg shadow-elevated"
                    style={{ zIndex: 1100 }}
                  >
                    {isLoadingSenders && (
                      <li className="px-3 py-2 text-sm text-fg-muted">{t("Buscando...")}</li>
                    )}
                    {!isLoadingSenders &&
                      senderSuggestions.map((it, idx) => (
                        <li
                          key={`${it.sender_id}-${it.companyId}-${idx}`}
                          role="option"
                          aria-selected={filters.senderId === it.sender_id}
                          onClick={() => onSenderSelect(it)}
                          className="px-3 py-2 rounded text-sm cursor-pointer text-fg hover:bg-bg-muted transition-colors"
                        >
                          <div className="font-semibold">{it.sender_id}</div>
                          <div className="text-fg-muted">
                            {it.companyName}
                            {it.integrationName ? ` · ${it.integrationName}` : ""}
                          </div>
                        </li>
                      ))}
                  </ul>
                )}
              </div>
            </div>
          </div>

          {/* Fechas */}
          <div
            className={fieldClass(!!filters?.dateStart || !!filters?.dateEnd)}
            role="group"
            aria-labelledby="filter-section-dates-label"
          >
            <div className="modern-filter-modal__field-header" id="filter-section-dates-label">
              <Calendar size={16} className="modern-filter-modal__field-icon" aria-hidden />
              <Label className="modern-filter-modal__label">{t("Fecha de ingreso")}</Label>
            </div>
            <DateRangeFilter
              compact
              value={datePreset}
              presets={DEFAULT_PRESETS}
              customRange={filters?.dateStart && filters?.dateEnd ? { start: filters.dateStart, end: filters.dateEnd } : null}
              onChange={(s, e) => setFilters({ ...filters, dateStart: s, dateEnd: e })}
              onClear={() => setFilters({ ...filters, dateStart: "", dateEnd: "" })}
            />
          </div>
        </div>

        <div className="modern-filter-modal__footer">
          <Button
            outline
            className="modern-filter-modal__btn modern-filter-modal__btn--secondary"
            onClick={clearFilters}
            type="button"
            disabled={!hasActiveFilters}
          >
            <RotateCcw size={16} aria-hidden />
            {t("Limpiar filtros")}
          </Button>
          <Button
            color="primary"
            className="modern-filter-modal__btn modern-filter-modal__btn--primary"
            onClick={applyFilters}
            type="button"
            aria-label={t("Aplicar filtros seleccionados y cerrar")}
          >
            {t("Aplicar filtros")}
          </Button>
        </div>
      </ModalBody>
    </Modal>
  );
};

export default HoldingFilterModal;
